import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

type SupporterVoicesMix = {
	itemsPerRow: number;
	maxRows: number;
	commentsPerVideo: number;
};

// Must match the middle-end's own cap on petitionSupporterVoicesFeed's maxItems
// (apps/middle-end/src/gql/types/PetitionSupporterVoice/queries/petitionSupporterVoicesFeed.ts).
// Validating it here means a misconfigured value falls back to the FCM's default (disabling the
// feed) instead of the middle-end silently truncating the response.
const MAX_ITEMS = 20;

function isPositiveInteger(value: number): boolean {
	return Number.isInteger(value) && value > 0;
}

const supporterVoicesMixNormalizer = (value: unknown) => {
	const mix = value as SupporterVoicesMix;

	if (!isPositiveInteger(mix.itemsPerRow)) {
		throw new Error('itemsPerRow must be a positive integer');
	}
	if (!isPositiveInteger(mix.maxRows)) {
		throw new Error('maxRows must be a positive integer');
	}
	if (!Number.isInteger(mix.commentsPerVideo) || mix.commentsPerVideo < 0) {
		throw new Error('commentsPerVideo must be a non-negative integer');
	}
	if (mix.itemsPerRow * mix.maxRows > MAX_ITEMS) {
		throw new Error(`itemsPerRow * maxRows must not exceed ${MAX_ITEMS}`);
	}

	return mix;
};

export const FCM_SUPPORTER_VOICES_MIX = defineFcm('supporter_voices_mix')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				itemsPerRow: { type: 'number' },
				maxRows: { type: 'number' },
				commentsPerVideo: { type: 'number' },
			},
			normalizer: supporterVoicesMixNormalizer,
		}),
	)
	.withDefault(undefined)
	.toConfig({
		owner: 'squad-vaquita',
		scope: 'corgi-front-end',
		context: 'Petition details',
		description: `Configures the mixed supporter voices feed on the petition page. Setting any value enables the feed (replacing the legacy comments + videos sections).
		- itemsPerRow: cards per row (combined with maxRows to cap the total item pool)
		- maxRows: maximum rows to display (total items = itemsPerRow * maxRows, must not exceed ${MAX_ITEMS})
		- commentsPerVideo: ratio of comment cards to video cards (1 = 1:1 alternating)`,
		import: false,
		template: {
			itemsPerRow: 3,
			maxRows: 2,
			commentsPerVideo: 1,
		},
	});
